import React from 'react';
import { Card, CardActionArea, Typography, Chip, Box, Stack } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PlaceIcon from '@mui/icons-material/Place';
import ShareIcon from '@mui/icons-material/Share';

const GRADIENTS = [
  'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)',
  'linear-gradient(135deg, #6a1b9a 0%, #ce93d8 100%)',
  'linear-gradient(135deg, #2e7d32 0%, #81c784 100%)',
  'linear-gradient(135deg, #e65100 0%, #ffb74d 100%)',
  'linear-gradient(135deg, #c62828 0%, #ef9a9a 100%)',
  'linear-gradient(135deg, #00695c 0%, #4db6ac 100%)',
];

function getGradient(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + hash * 31;
  return GRADIENTS[Math.abs(hash) % GRADIENTS.length];
}

export default function TripCard({ trip, status, onClick }) {
  const activityCount = trip.activities?.length || 0;
  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return (
    <Card
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 30px rgba(0,0,0,0.18)' },
      }}
    >
      <CardActionArea onClick={onClick}>
        {/* Hero Section */}
        <Box sx={{ position: 'relative', height: 180 }}>
          {trip.coverPhotoUrl ? (
            <Box
              component="img"
              src={trip.coverPhotoUrl}
              alt={trip.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Box sx={{ width: '100%', height: '100%', background: getGradient(trip.name) }} />
          )}

          {/* Gradient overlay */}
          <Box
            sx={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)',
            }}
          />

          {/* Activity count badge */}
          <Chip
            label={`${activityCount} ${activityCount === 1 ? 'place' : 'places'}`}
            size="small"
            icon={<PlaceIcon style={{ fontSize: 13, color: 'white' }} />}
            sx={{
              position: 'absolute', top: 12, right: 12,
              bgcolor: 'rgba(0,0,0,0.45)', color: 'white',
              backdropFilter: 'blur(4px)', fontWeight: 600, fontSize: 11,
            }}
          />

          {/* Share code badge */}
          {trip.shareCode && (
            <Chip
              label={trip.shareCode}
              size="small"
              icon={<ShareIcon style={{ fontSize: 11, color: 'white' }} />}
              sx={{
                position: 'absolute', top: 12, left: 12,
                bgcolor: 'rgba(0,0,0,0.45)', color: 'white',
                backdropFilter: 'blur(4px)', fontWeight: 700, fontSize: 11, letterSpacing: 1,
              }}
            />
          )}

          {/* Status chip */}
          {status && (
            <Chip
              label={status.label}
              size="small"
              color={status.color}
              sx={{
                position: 'absolute', top: trip.shareCode ? 42 : 12, left: 12,
                fontWeight: 600, fontSize: 11,
              }}
            />
          )}

          {/* Trip name overlaid */}
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2 }}>
            <Typography variant="h6" fontWeight="bold" color="white" noWrap sx={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
              {trip.name}
            </Typography>
            {(trip.startDate || trip.endDate) && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CalendarTodayIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  {formatDate(trip.startDate)}
                  {trip.endDate && ` – ${formatDate(trip.endDate)}`}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>

        {/* Description */}
        {trip.description && (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary" noWrap>
              {trip.description}
            </Typography>
          </Box>
        )}
      </CardActionArea>
    </Card>
  );
}
